import { ApiResponse } from "../types";
import { apiClient } from "./apiClient";

// Helper function to format time to HH:MM
const formatTimeToHHMM = (time: string): string => {
  if (!time) return time;

  // If time already in HH:MM format, return as is
  if (time.length === 5 && time.includes(":")) {
    return time;
  }

  // If time in HH:MM:SS format, remove seconds
  if (time.length === 8 && time.split(":").length === 3) {
    return time.substring(0, 5);
  }

  return time;
};

export interface AvailabilityInterval {
  start: string;
  end: string;
}

export interface Availability {
  id: number;
  companyId: number;
  professionalId?: number;
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AvailabilityData {
  monday: { open: boolean; intervals: AvailabilityInterval[] };
  tuesday: { open: boolean; intervals: AvailabilityInterval[] };
  wednesday: { open: boolean; intervals: AvailabilityInterval[] };
  thursday: { open: boolean; intervals: AvailabilityInterval[] };
  friday: { open: boolean; intervals: AvailabilityInterval[] };
  saturday: { open: boolean; intervals: AvailabilityInterval[] };
  sunday: { open: boolean; intervals: AvailabilityInterval[] };
}

export const availabilityApi = {
  // Get availability for a professional
  async getAvailabilityByProfessional(
    professionalId: number
  ): Promise<ApiResponse<AvailabilityData>> {
    try {
      const data = await apiClient.get<any>(
        `/availability/professional/${professionalId}`
      );

      // Convert API response to our component format
      const availabilityData: AvailabilityData = {
        monday: { open: false, intervals: [] },
        tuesday: { open: false, intervals: [] },
        wednesday: { open: false, intervals: [] },
        thursday: { open: false, intervals: [] },
        friday: { open: false, intervals: [] },
        saturday: { open: false, intervals: [] },
        sunday: { open: false, intervals: [] },
      };

      // Day mapping: 0=Sunday, 1=Monday, ..., 6=Saturday
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];

      // Group availability by day of week
      if (data.data && Array.isArray(data.data)) {
        const availabilityByDay: { [key: number]: Availability[] } = {};

        data.data.forEach((availability: Availability) => {
          if (availability.isActive) {
            if (!availabilityByDay[availability.dayOfWeek]) {
              availabilityByDay[availability.dayOfWeek] = [];
            }
            availabilityByDay[availability.dayOfWeek].push(availability);
          }
        });

        // Convert to our format
        Object.entries(availabilityByDay).forEach(
          ([dayOfWeek, availabilities]) => {
            const dayName = dayNames[
              parseInt(dayOfWeek)
            ] as keyof AvailabilityData;
            if (dayName && availabilityData[dayName]) {
              availabilityData[dayName] = {
                open: availabilities.length > 0,
                intervals: availabilities.map((a) => ({
                  start: formatTimeToHHMM(a.startTime),
                  end: formatTimeToHHMM(a.endTime),
                })),
              };
            }
          }
        );
      }

      return {
        success: true,
        data: availabilityData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get availability for a company
  async getAvailabilityByCompany(): Promise<ApiResponse<AvailabilityData>> {
    try {
      const data = await apiClient.get<any>(
        `/availability/company`
      );

      // Convert API response to our component format
      const availabilityData: AvailabilityData = {
        monday: { open: false, intervals: [] },
        tuesday: { open: false, intervals: [] },
        wednesday: { open: false, intervals: [] },
        thursday: { open: false, intervals: [] },
        friday: { open: false, intervals: [] },
        saturday: { open: false, intervals: [] },
        sunday: { open: false, intervals: [] },
      };

      // Day mapping: 0=Sunday, 1=Monday, ..., 6=Saturday
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];

      // Group availability by day of week
      if (data.data && Array.isArray(data.data)) {
        const availabilityByDay: { [key: number]: Availability[] } = {};

        data.data.forEach((availability: Availability) => {
          if (availability.isActive) {
            if (!availabilityByDay[availability.dayOfWeek]) {
              availabilityByDay[availability.dayOfWeek] = [];
            }
            availabilityByDay[availability.dayOfWeek].push(availability);
          }
        });

        // Convert to our format
        Object.entries(availabilityByDay).forEach(
          ([dayOfWeek, availabilities]) => {
            const dayName = dayNames[
              parseInt(dayOfWeek)
            ] as keyof AvailabilityData;
            if (dayName && availabilityData[dayName]) {
              availabilityData[dayName] = {
                open: availabilities.length > 0,
                intervals: availabilities.map((a) => ({
                  start: formatTimeToHHMM(a.startTime),
                  end: formatTimeToHHMM(a.endTime),
                })),
              };
            }
          }
        );
      }

      return {
        success: true,
        data: availabilityData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get available slots for a professional and service
  async getAvailableSlotsByProfessional(
    professionalId: number,
    serviceId: number,
    date?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      let endpoint = `/availability/slots/professional/${professionalId}/${serviceId}`;
      if (date) {
        endpoint += `?date=${date}`;
      }

      const data = await apiClient.get<any>(endpoint);

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get available slots for a professional and service
  async getAvailableSlotsByCompany(
    serviceId: number,
    date?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      let endpoint = `/availability/slots/company/service/${serviceId}`;
      if (date) {
        endpoint += `?date=${date}`;
      }

      const data = await apiClient.get<any>(endpoint);

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get all slots for a professional (includes availability, services, and slots)
  async getAllSlots(
    professionalId: number,
    date?: string
  ): Promise<ApiResponse<any>> {
    try {
      let endpoint = `/availability/slots/all?professionalId=${professionalId}`;
      if (date) {
        endpoint += `&date=${date}`;
      }

      const data = await apiClient.get<any>(endpoint);

      return {
        success: true,
        data: data.data || {},
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get availability by ID
  async getAvailabilityById(id: number): Promise<ApiResponse<Availability>> {
    try {
      const data = await apiClient.get<any>(`/availability/${id}`);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Create a single availability slot
  async createAvailabilitySlot(
    professionalId: number | undefined,
    dayOfWeek: number,
    startTime: string,
    endTime: string
  ): Promise<ApiResponse<Availability>> {
    try {
      const requestBody: any = {
        dayOfWeek,
        startTime,
        endTime,
        isActive: true,
      };
      if (professionalId) {
        requestBody.professionalId = professionalId;
      }
      const data = await apiClient.post<any>("/availability", requestBody);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Update a single availability slot
  async updateAvailabilitySlot(
    id: number,
    updates: {
      dayOfWeek?: number;
      startTime?: string;
      endTime?: string;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<Availability>> {
    try {
      const data = await apiClient.put<any>(`/availability/${id}`, updates);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete a single availability slot
  async deleteAvailabilitySlot(id: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/availability/${id}`);

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete all availability slots for a professional
  async deleteAllAvailabilityByProfessional(
    professionalId: number
  ): Promise<ApiResponse<void>> {
    try {
      const data = await apiClient.get<any>(
        `/availability/professional/${professionalId}`
      );

      if (data.data && Array.isArray(data.data)) {
        for (const availability of data.data) {
          await this.deleteAvailabilitySlot(availability.id);
        }
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete all availability slots for a company
  async deleteAllAvailabilityByCompany(
    companyId: number
  ): Promise<ApiResponse<void>> {
    try {
      const data = await apiClient.get<any>(
        `/availability/company/${companyId}`
      );

      if (data.data && Array.isArray(data.data)) {
        for (const availability of data.data) {
          await this.deleteAvailabilitySlot(availability.id);
        }
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Update availability for a professional (bulk operation for convenience)
  async updateAvailabilityByProfessional(
    professionalId: number,
    availabilityData: AvailabilityData
  ): Promise<ApiResponse<Availability[]>> {
    try {
      // First, get existing availability
      const existingResponse = await this.getAvailabilityByProfessional(
        professionalId
      );
      if (!existingResponse.success) {
        throw new Error(
          existingResponse.error || "Failed to get existing availability"
        );
      }
      const existingData = existingResponse.data;

      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const resultAvailabilities: Availability[] = [];

      // Get current availability from backend to compare IDs
      const currentData = await apiClient.get<any>(
        `/availability/professional/${professionalId}`
      );
      const currentAvailabilities = currentData.data || [];

      for (const [dayName, dayData] of Object.entries(availabilityData)) {
        const dayOfWeek = dayNames.indexOf(dayName);
        if (dayOfWeek === -1) continue;

        const existingDayData =
          existingData?.[dayName as keyof AvailabilityData];
        const currentDayAvailabilities = currentAvailabilities.filter(
          (a: Availability) => a.dayOfWeek === dayOfWeek && a.isActive
        );

        // If day is closed now, delete all existing intervals for this day
        if (!dayData.open) {
          for (const availability of currentDayAvailabilities) {
            await this.deleteAvailabilitySlot(availability.id);
          }
          continue;
        }

        // Compare existing intervals with new ones
        const newIntervals = dayData.intervals;
        const existingIntervals = existingDayData?.intervals || [];

        // Check if intervals have changed
        const intervalsChanged =
          newIntervals.length !== existingIntervals.length ||
          newIntervals.some((newInterval: any, index: number) => {
            const existingInterval = existingIntervals[index];
            return (
              !existingInterval ||
              newInterval.start !== existingInterval.start ||
              newInterval.end !== existingInterval.end
            );
          });

        if (intervalsChanged) {
          // Delete all existing intervals for this day
          for (const availability of currentDayAvailabilities) {
            await this.deleteAvailabilitySlot(availability.id);
          }

          // Create new intervals
          for (const interval of newIntervals) {
            const response = await this.createAvailabilitySlot(
              professionalId,
              dayOfWeek,
              interval.start,
              interval.end
            );
            if (response.success && response.data) {
              resultAvailabilities.push(response.data);
            }
          }
        } else {
          // No changes, keep existing
          resultAvailabilities.push(...currentDayAvailabilities);
        }
      }

      return {
        success: true,
        data: resultAvailabilities,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Update availability for a company (bulk operation for convenience)
  async updateAvailabilityByCompany(
    availabilityData: AvailabilityData
  ): Promise<ApiResponse<Availability[]>> {
    try {
      // First, get existing availability
      const existingResponse = await this.getAvailabilityByCompany();
      if (!existingResponse.success) {
        throw new Error(
          existingResponse.error || "Failed to get existing availability"
        );
      }
      const existingData = existingResponse.data;

      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const resultAvailabilities: Availability[] = [];

      // Get current availability from backend to compare IDs
      const currentData = await apiClient.get<any>(
        `/availability/company`
      );
      const currentAvailabilities = currentData.data || [];

      for (const [dayName, dayData] of Object.entries(availabilityData)) {
        const dayOfWeek = dayNames.indexOf(dayName);
        if (dayOfWeek === -1) continue;

        const existingDayData =
          existingData?.[dayName as keyof AvailabilityData];
        const currentDayAvailabilities = currentAvailabilities.filter(
          (a: Availability) => a.dayOfWeek === dayOfWeek && a.isActive
        );

        // If day is closed now, delete all existing intervals for this day
        if (!dayData.open) {
          for (const availability of currentDayAvailabilities) {
            await this.deleteAvailabilitySlot(availability.id);
          }
          continue;
        }

        // Compare existing intervals with new ones
        const newIntervals = dayData.intervals;
        const existingIntervals = existingDayData?.intervals || [];

        // Check if intervals have changed
        const intervalsChanged =
          newIntervals.length !== existingIntervals.length ||
          newIntervals.some((newInterval: any, index: number) => {
            const existingInterval = existingIntervals[index];
            return (
              !existingInterval ||
              newInterval.start !== existingInterval.start ||
              newInterval.end !== existingInterval.end
            );
          });

        if (intervalsChanged) {
          // Delete all existing intervals for this day
          for (const availability of currentDayAvailabilities) {
            await this.deleteAvailabilitySlot(availability.id);
          }

          // Create new intervals
          for (const interval of newIntervals) {
            const response = await this.createAvailabilitySlot(
              undefined, // No specific professional for company-wide availability
              dayOfWeek,
              interval.start,
              interval.end
            );
            if (response.success && response.data) {
              resultAvailabilities.push(response.data);
            }
          }
        } else {
          // No changes, keep existing
          resultAvailabilities.push(...currentDayAvailabilities);
        }
      }

      return {
        success: true,
        data: resultAvailabilities,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },
};
